import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Loader2, RefreshCw, Link2, CheckCircle2, AlertCircle } from "lucide-react";

type StatsProvider = {
  id: string;
  name: string;
  providerType: string;
  logoUrl?: string;
  description?: string;
  isActive: boolean;
};

type AthleteStatsPlatform = {
  id: string;
  athleteId: string;
  providerId: string;
  externalAthleteId: string;
  externalTeamId?: string;
  username?: string;
  lastSyncedAt?: string;
  syncEnabled: boolean;
};

type GameStat = {
  id: string;
  athleteId: string;
  providerId: string;
  externalGameId: string;
  gameDate: string;
  opponent?: string;
  location?: string;
  result?: string;
  score?: string;
  sportType: string;
  stats: any;
  highlights?: string;
};

type SeasonStat = {
  id: string;
  athleteId: string;
  providerId: string;
  season: string;
  startDate?: string;
  endDate?: string;
  teamName?: string;
  sportType: string;
  stats: any;
  highlights?: string;
};

interface StatsIntegrationProps {
  athleteId: string;
}

const StatsIntegration: React.FC<StatsIntegrationProps> = ({ athleteId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("platforms");
  const [newPlatformData, setNewPlatformData] = useState({
    providerId: "",
    externalAthleteId: "",
    externalTeamId: "",
    username: "",
  });

  // Fetch available stats providers
  const { data: providers, isLoading: isLoadingProviders } = useQuery({
    queryKey: ["/api/stats/providers"],
    select: (data: StatsProvider[]) => data,
  });

  // Fetch athlete's connected platforms
  const { data: connectedPlatforms, isLoading: isLoadingPlatforms } = useQuery({
    queryKey: [`/api/stats/athlete-platforms/${athleteId}`],
    select: (data: AthleteStatsPlatform[]) => data,
  });

  // Fetch game stats
  const { data: gameStats, isLoading: isLoadingGameStats } = useQuery({
    queryKey: [`/api/stats/games/${athleteId}`],
    select: (data: GameStat[]) => data,
  });

  // Fetch season stats
  const { data: seasonStats, isLoading: isLoadingSeasonStats } = useQuery({
    queryKey: [`/api/stats/seasons/${athleteId}`],
    select: (data: SeasonStat[]) => data,
  });

  // Connect new platform mutation
  const connectPlatformMutation = useMutation({
    mutationFn: async (data: typeof newPlatformData & { athleteId: string }) => {
      const response = await fetch("/api/stats/connect-platform", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to connect platform");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Platform Connected",
        description: "Successfully connected to the stats platform.",
      });
      setNewPlatformData({
        providerId: "",
        externalAthleteId: "",
        externalTeamId: "",
        username: "",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/stats/athlete-platforms/${athleteId}`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to connect platform",
        variant: "destructive",
      });
    },
  });

  // Sync platform data mutation
  const syncPlatformMutation = useMutation({
    mutationFn: async (platformId: string) => {
      const response = await fetch(`/api/stats/sync-platform/${platformId}`, {
        method: "POST",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to sync platform data");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Sync Started",
        description: "Stats synchronization has been initiated.",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/stats/games/${athleteId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/stats/seasons/${athleteId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/stats/athlete-platforms/${athleteId}`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Sync Error",
        description: error.message || "Failed to sync platform data",
        variant: "destructive",
      });
    },
  });

  // Handle connect platform form submission
  const handleConnectPlatform = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPlatformData.providerId || !newPlatformData.externalAthleteId) {
      toast({
        title: "Validation Error",
        description: "Provider and Athlete ID are required",
        variant: "destructive",
      });
      return;
    }

    connectPlatformMutation.mutate({
      ...newPlatformData,
      athleteId,
    });
  };

  // Handle sync platform data
  const handleSync = (platformId: string) => {
    syncPlatformMutation.mutate(platformId);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Sports Statistics Integration</CardTitle>
        <CardDescription>
          Connect to popular sports statistics platforms to automatically import game and season stats.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="platforms" className="flex-1 whitespace-nowrap">
              <span className="truncate">Connected Platforms</span>
            </TabsTrigger>
            <TabsTrigger value="game-stats" className="flex-1 whitespace-nowrap">
              <span className="truncate">Game Stats</span>
            </TabsTrigger>
            <TabsTrigger value="season-stats" className="flex-1 whitespace-nowrap">
              <span className="truncate">Season Stats</span>
            </TabsTrigger>
          </TabsList>

          {/* Connected Platforms Tab */}
          <TabsContent value="platforms">
            <div className="space-y-6">
              <div className="grid gap-4">
                <h3 className="text-lg font-medium">Connected Platforms</h3>
                
                {isLoadingPlatforms ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : connectedPlatforms && connectedPlatforms.length > 0 ? (
                  <div className="grid gap-4">
                    {connectedPlatforms.map((platform) => (
                      <Card key={platform.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              {providers?.find(p => p.id === platform.providerId)?.logoUrl && (
                                <img 
                                  src={providers?.find(p => p.id === platform.providerId)?.logoUrl} 
                                  alt={providers?.find(p => p.id === platform.providerId)?.name} 
                                  className="h-8 w-8 object-contain"
                                />
                              )}
                              <div>
                                <h4 className="font-medium">{providers?.find(p => p.id === platform.providerId)?.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  Username: {platform.username || 'Not specified'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {platform.lastSyncedAt ? (
                                <span className="text-xs text-muted-foreground">
                                  Last synced: {new Date(platform.lastSyncedAt).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  Never synced
                                </span>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleSync(platform.id)}
                                disabled={syncPlatformMutation.isPending}
                              >
                                {syncPlatformMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                )}
                                Sync Now
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No platforms connected yet.
                  </div>
                )}
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-medium mb-4">Connect New Platform</h3>
                <form onSubmit={handleConnectPlatform} className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="provider">Select Platform</Label>
                    <select
                      id="provider"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      value={newPlatformData.providerId}
                      onChange={(e) => setNewPlatformData({ ...newPlatformData, providerId: e.target.value })}
                      required
                    >
                      <option value="">Select a platform</option>
                      {providers?.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="externalAthleteId">Athlete ID / Profile ID</Label>
                    <Input
                      id="externalAthleteId"
                      placeholder="Enter the athlete ID from the platform"
                      value={newPlatformData.externalAthleteId}
                      onChange={(e) => setNewPlatformData({ ...newPlatformData, externalAthleteId: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="externalTeamId">Team ID (Optional)</Label>
                    <Input
                      id="externalTeamId"
                      placeholder="Enter team ID if applicable"
                      value={newPlatformData.externalTeamId}
                      onChange={(e) => setNewPlatformData({ ...newPlatformData, externalTeamId: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="username">Username (Optional)</Label>
                    <Input
                      id="username"
                      placeholder="Enter username on the platform"
                      value={newPlatformData.username}
                      onChange={(e) => setNewPlatformData({ ...newPlatformData, username: e.target.value })}
                    />
                  </div>
                  
                  <Button type="submit" disabled={connectPlatformMutation.isPending}>
                    {connectPlatformMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Connect Platform
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>

          {/* Game Stats Tab */}
          <TabsContent value="game-stats">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Game Statistics</h3>
              
              {isLoadingGameStats ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : gameStats && gameStats.length > 0 ? (
                <div className="space-y-4">
                  {gameStats.map((game) => (
                    <Card key={game.id}>
                      <CardContent className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium">{game.opponent ? `vs ${game.opponent}` : 'Game'}</h4>
                            <p className="text-sm text-muted-foreground">
                              {new Date(game.gameDate).toLocaleDateString()} 
                              {game.location && ` • ${game.location}`}
                            </p>
                            {game.result && game.score && (
                              <p className={`text-sm mt-1 ${game.result === 'Win' ? 'text-green-600' : game.result === 'Loss' ? 'text-red-600' : 'text-yellow-600'}`}>
                                {game.result}: {game.score}
                              </p>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium">Key Stats:</h5>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {Object.entries(game.stats || {}).slice(0, 6).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-xs text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                  <span className="text-xs font-medium">{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No game statistics available. Connect a stats platform and sync data to see game statistics.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Season Stats Tab */}
          <TabsContent value="season-stats">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Season Statistics</h3>
              
              {isLoadingSeasonStats ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : seasonStats && seasonStats.length > 0 ? (
                <div className="space-y-4">
                  {seasonStats.map((season) => (
                    <Card key={season.id}>
                      <CardContent className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium">{season.season}</h4>
                            <p className="text-sm text-muted-foreground">
                              {season.teamName || 'Team'} • {season.sportType}
                            </p>
                            {season.startDate && season.endDate && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(season.startDate).toLocaleDateString()} to {new Date(season.endDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium">Season Stats:</h5>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {Object.entries(season.stats || {}).slice(0, 8).map(([key, value]) => (
                                <div key={key} className="flex justify-between">
                                  <span className="text-xs text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                  <span className="text-xs font-medium">{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No season statistics available. Connect a stats platform and sync data to see season statistics.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default StatsIntegration;